"use client"

import { BarChart3, Search, Focus, BookOpen, RefreshCw, ChevronRight } from "lucide-react"

const features = [
  {
    icon: <BarChart3 className="h-5 w-5 text-indigo-600" />,
    title: "Data Science That Goes Deeper",
    points: [
      "We analyse thousands of listed companies, scanning decades of financial data",
      "Millions of scenarios are simulated to assess performance in different macro environments",
      "Our machine learning models continuously optimise for portfolio performance"
    ]
  },
  {
    icon: <Search className="h-5 w-5 text-indigo-600" />,
    title: "Smart Scoring Models",
    points: [
      "We map complex relationships between financial variables",
      "Our models detect non-linear correlations that traditional methods miss",
      "The result? Diversified portfolios built for low drawdowns and faster recovery"
    ]
  },
  {
    icon: <Focus className="h-5 w-5 text-indigo-600" />,
    title: "Dimensionality Reduction",
    points: [
      "We use refined Principal Component Analysis (PCA) to distil large datasets",
      "Our algorithms zoom in on factors that truly move company performance",
      "This cuts out the noise and improves prediction accuracy"
    ]
  },
  {
    icon: <BookOpen className="h-5 w-5 text-indigo-600" />,
    title: "AI That Reads Beyond Numbers",
    points: [
      "Our custom-built RAG pipelines digest unstructured data",
      "We extract insights from research reports, earnings calls, and more",
      "Hosted on a secure, on-premise system to protect data and insights"
    ]
  },
  {
    icon: <RefreshCw className="h-5 w-5 text-indigo-600" />,
    title: "Continuous Portfolio Optimisation",
    points: [
      "Monitor markets and company-specific indicators in real time",
      "Trigger alerts based on pre-set thresholds",
      "Enable timely action without emotional bias or reactive behaviour"
    ],
    footnote: "We combine a “buy and hold” mindset with technology-enabled vigilance."
  }
]

export default function AIInvestmentStack() {
  return (
    <section className="relative w-full z-10 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="text-center mb-16">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
          Our AI-Powered Investment Stack
        </h2>
        <p className="mt-4 max-w-3xl mx-auto text-lg text-gray-600">
          Maxiom Wealth's competitive edge lies in how we translate philosophy into action. Our proprietary AI ecosystem converts the Roots & Wings framework into data-backed investment decisions.
        </p>
      </div>

      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
        {features.map((feature, idx) => (
          <div
            key={idx}
            className="relative p-6 bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-blue-100 p-2 rounded-full">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
            </div>
            <ul className="space-y-3 text-gray-700 text-sm leading-relaxed">
              {feature.points.map((point, i) => (
                <li key={i} className="flex items-start">
                  <ChevronRight className="h-4 w-4 text-indigo-500 mt-1" />
                  <span className="ml-2">{point}</span>
                </li>
              ))}
            </ul>
            {feature.footnote && (
              <p className="mt-4 italic text-gray-600 text-sm">
                {feature.footnote}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
